'use client'

import { ErrorMessage } from '@/components/ui/error-message'
import { Label } from '@/components/ui/label'
import { cn } from '@/utils/styles/cn'
import * as SwitchPrimitives from '@radix-ui/react-switch'
import { AnimatePresence, motion } from 'framer-motion'
import * as React from 'react'
import { useMemo } from 'react'

interface ISwitchProps extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> {
    destructive?: boolean
}

export const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, ISwitchProps>(
    ({ className, destructive, ...props }, ref) => (
        <SwitchPrimitives.Root
            className={cn(
                `focus-visible:ring-ring focus-visible:ring-offset-background data-[state=unchecked]:bg-input dark:data-[state=unchecked]:bg-primary/40 peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-xs transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40`,
                destructive ? 'data-[state=checked]:bg-destructive' : 'data-[state=checked]:bg-[#e16a00]',
                className
            )}
            {...props}
            ref={ref}
        >
            <SwitchPrimitives.Thumb
                className={cn(
                    `bg-background pointer-events-none block h-4 w-4 rounded-full ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0`
                )}
            />
        </SwitchPrimitives.Root>
    )
)

Switch.displayName = SwitchPrimitives.Root.displayName

interface ISwitchWithLabelProps extends ISwitchProps {
    label: string
    className?: string
    error?: string | null
    labelAlign?: 'left' | 'right'
}

export const SwitchWithLabel = ({ label, className, error, labelAlign = 'right', ...props }: ISwitchWithLabelProps) => {
    const componentId = React.useId()

    const labelComponent = useMemo(() => {
        return (
            <Label className="text-sm font-medium text-[#474747] whitespace-nowrap">
                <AnimatePresence initial={false} mode="popLayout">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.1 }}
                        key={'switch-label' + componentId + label}
                    >
                        {label}
                    </motion.div>
                </AnimatePresence>
            </Label>
        )
    }, [label, componentId])

    return (
        <div className={cn('flex flex-col', className)}>
            <div className={cn('flex items-center gap-2')}>
                {labelAlign === 'left' && labelComponent}

                <Switch {...props} />

                {labelAlign === 'right' && labelComponent}
            </div>

            <AnimatePresence initial={false}>{error && <ErrorMessage error={error} />}</AnimatePresence>
        </div>
    )
}
